import { apiFetch, IDENTIDAD_MS, PENDIENTES_MS } from "./client";
// La red contra el backend viejo es UNA sola, compartida con el organizador:
// dos copias de la misma cuenta se desincronizan.
import { normalizarValla } from "./organizer";

/**
 * Cliente de los endpoints del FLUJO JUGADOR (/api/v1/me/*) — los mismos
 * que consume la app Android. Todos van scopeados al CONTEXTO ACTIVO del
 * usuario (liga + torneo + equipo), igual que en mobile.
 *
 * CONVENCIÓN DE HORA: `scheduledAt` guarda la hora de pared de la liga
 * COMO SI FUERA UTC → mostrar SIEMPRE con timeZone: "UTC", nunca convertir
 * a hora local (ver memoria convencion-hora-de-pared).
 *
 * Área de Claude — Codex no toca este archivo (ver AGENTS.md).
 */

export type PlayerNextMatch = {
  id: string;
  scheduledAt: string | null;
  /**
   * PROGRAMACIÓN PROGRESIVA: el día de la jornada ("YYYY-MM-DD") cuando el
   * partido todavía no tiene hora. El fixture se sortea entero y se programa
   * semana a semana, así que el próximo partido normalmente llega sin horario
   * y a veces sin día: la card se muestra igual, con lo que sí se sabe.
   */
  matchdayStartsOn?: string | null;
  matchdayLabel?: string | null;
  matchdaySequence?: number | null;
  venueName: string | null;
  homeTeamName: string;
  awayTeamName: string;
  opponentName: string;
  myTeamLogoUrl: string | null;
  opponentLogoUrl: string | null;
  isHome: boolean;
  status: string;
  /** Aviso del organizador ("suspendido por lluvia"): el porqué del estado. */
  publicNote?: string | null;
  /**
   * Fase del partido. "bye" = el amistoso entre los LIBRES de la fecha: partido
   * real pero fuera de las estadísticas, con su propia regla de puntaje
   * (`byeMode`). Es la marca ESTRUCTURAL — antes el jugador sólo tenía el
   * `publicNote`, que se perdía al reprogramar (ver lib/amistosoLibres.ts).
   */
  stageCode?: string | null;
  /** Regla del amistoso de libres: "double_win" (o null = default) / "real_result". */
  byeMode?: string | null;
  /** Colores de camiseta de ambos (migración 072) + veredicto del server. */
  homeKitColor?: string | null;
  homeKitColor2?: string | null;
  homeKitDesign?: string | null;
  homeKitAlt?: string | null;
  awayKitColor?: string | null;
  awayKitColor2?: string | null;
  awayKitDesign?: string | null;
  awayKitAlt?: string | null;
  kitAviso?: { sePisan: boolean; cambia: "home" | "away" | null; alternativa: string | null } | null;
  /**
   * EL MARCADOR. Sólo viene cargado en `lastResult` (el último partido jugado)
   * y en el partido en juego; en un partido programado es null — y null NO es
   * 0-0, es "nadie lo cargó" (ver `getLastResultMatch` en el backend, que
   * justamente exige los dos no-null para dar el partido por terminado).
   */
  homeScore?: number | null;
  awayScore?: number | null;
  /** Definición por penales del mata-mata: dice quién pasó con el 1-1. */
  homeScorePenalties?: number | null;
  awayScorePenalties?: number | null;
  /**
   * Cuándo terminó, instante REAL en UTC (ojo: `scheduledAt` es hora de
   * pared). Es de donde sale la ventana de 48 h de `destacado`, que el server
   * ya resolvió: acá viaja para poder auditar la decisión, no para recalcularla.
   */
  finishedAt?: string | null;
};

export type PlayerStatsSummary = {
  matchesPlayed: number;
  minutesPlayed: number;
  goals: number;
  assists: number;
  yellowCards: number;
};

export type PlayerStandingsRow = {
  teamId: string;
  groupName: string | null;
  position: number;
  teamName: string;
  logoUrl: string | null;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
  recentResults: string[];
  /**
   * Estado de inscripción. "withdrawn" = equipo RETIRADO del torneo: la fila
   * sigue en la tabla (sus W.O. ya cuentan para todos) pero va apagada y con
   * "RETIRADO" en lugar de la racha. Opcional: una respuesta vieja en caché o
   * la tabla anual no lo traen.
   */
  entryStatus?: string | null;
};

/**
 * TABLA ANUAL (099): la suma de los torneos de la temporada (Apertura +
 * Intermedio + Clausura). Viaja DENTRO de la tabla porque es la misma tabla
 * vista de otra forma. null = este torneo no suma con ninguno.
 */
export type PlayerAnnual = {
  annualTableId: string;
  name: string;
  tournaments: { id: string; name: string; season: string; status: string }[];
  tieBreak?: { key: string; label: string }[];
  rows: PlayerStandingsRow[];
  /** Campeón de la temporada (100), cuando ya salió. */
  champion?: { teamId: string; teamName: string; logoUrl: string | null; reason: string } | null;
  /** Zonas de ascenso/descenso de la temporada. */
  zonas?: {
    promoteCount: number | null;
    promotesToName: string | null;
    relegateCount: number | null;
    relegatesToName: string | null;
  } | null;
};

export type PlayerStandings = {
  tournamentName: string | null;
  qualifyCount: number | null;
  annual?: PlayerAnnual | null;
  /**
   * Cómo se ordena la tabla, ya en castellano y con "Puntos" adelante. Lo arma
   * el motor (describirDesempate) para que las tres superficies digan lo mismo.
   * Opcional: una respuesta vieja en caché no rompe la tabla.
   */
  tieBreak?: { key: string; label: string }[];
  /**
   * EL CAMPEÓN DEL TORNEO (no el de la temporada, que va en `annual`). Viaja
   * con la tabla porque es donde se lee: arriba de la tabla final. null hasta
   * que el organizador finaliza el torneo — el candado es `campeonAnunciable`
   * en el backend, y proclamarlo es justamente el gesto de cerrarlo.
   */
  champion?: { teamId: string; teamName: string; logoUrl: string | null; reason: string } | null;
  rows: PlayerStandingsRow[];
};

export type PlayerNewsItem = {
  id?: string;
  title: string;
  body?: string | null;
  type?: string;
  publishedAt: string;
  /** La imagen de la novedad (la card del Equipo Ideal): es lo que se
   *  comparte. Opcional: una novedad de texto no la trae. */
  imageUrl?: string | null;
};

export type PlayerHome = {
  /** true = NO juega en el contexto activo (seguidor, hincha o jugador
   *  explorando otro torneo). Deriva de `membership`, que es el flag fino. */
  isSpectator: boolean;
  membership?: "player" | "fan" | "browse_only" | "spectator";
  /** DT de su equipo (138): el Home lo rotula "DT". */
  isCoach?: boolean;
  /** Gate de escritura en Comunidad (ficha en alguna liga), calculado por el
   *  backend con el MISMO criterio que su 403. */
  communityCanPost?: boolean;
  player: {
    id: string;
    displayName: string | null;
    position: string | null;
    jerseyNumber: number | null;
    photoUrl: string | null;
  };
  context: {
    /** Ids del contexto con el que el server COMPUTÓ la respuesta (auditoría
     *  del contexto, 13-09-2026). Ausentes con backend viejo. */
    leagueId?: string | null;
    tournamentId?: string | null;
    leagueName: string | null;
    tournamentName: string | null;
    teamId: string | null;
    teamName: string | null;
  };
  nextMatch: PlayerNextMatch | null;
  /**
   * EL ÚLTIMO PARTIDO JUGADO, con su resultado oficial. Viaja siempre que
   * exista, esté o no destacado: cuando manda el próximo partido, el resultado
   * baja a la tira compacta en vez de perderse.
   */
  lastResult?: PlayerNextMatch | null;
  /**
   * QUIÉN MANDA EN EL HERO. Lo decide el SERVER (destacadoDelHome): el
   * resultado vive 48 h desde que terminó el partido, salvo que el próximo
   * arranque en menos de 48 h o haya uno en juego. Acá no se recalcula — si
   * cada cliente contara sus horas, la app y la PWA podrían discrepar sobre el
   * mismo domingo. Ausente (backend viejo) = "proximo", lo de siempre.
   */
  destacado?: "proximo" | "resultado";
  statsSummary: PlayerStatsSummary;
  standingsPreview: PlayerStandings;
  news: PlayerNewsItem[];
  /** Banco de fotos (103): banner "encontrá tu foto". null = no corresponde
   *  (ya tiene foto de ficha, sin equipo, o el banco está vacío). El equipo
   *  viene en el dato porque puede NO ser el del contexto activo: el lote se
   *  sube por equipo y el jugador de dos ligas tenía el banner invisible. */
  photoBank?: { availableCount: number; teamId: string; teamName: string } | null;
  /**
   * "Te toca cargar la planilla" del próximo partido. null = no corresponde
   * (no soy capitán/delegado de ese equipo, la planilla ya está confirmada, o
   * el partido ya no admite edición). Lo decide el backend con las MISMAS
   * reglas que la pantalla de planilla: si acá hay puerta, allá se puede.
   */
  planillaPendiente?: {
    canEdit: boolean;
    isLoaded: boolean;
    playerCount: number;
    side: "home" | "away";
  } | null;
};

export type PlayerMatchRow = {
  id: string;
  scheduledAt: string | null;
  venueName: string | null;
  /**
   * POR QUE es W.O.: "no_show" (planton) o "withdrawal" (el rival se retiro del
   * torneo). Sin esto las dos cosas se ven igual, y al que se bajo nadie lo
   * estaba esperando.
   */
  walkoverReason?: string | null;
  matchdayId: string | null;
  matchdayLabel: string | null;
  matchdaySequence: number | null;
  status: string;
  homeScore: number | null;
  awayScore: number | null;
  /** Definición por penales (sólo mata-mata empatado): dice quién pasó. */
  homeScorePenalties?: number | null;
  awayScorePenalties?: number | null;
  liveMinute: number | null;
  /** Número del tiempo en curso (1 = 1T, 2 = 2T…); null sin acta de períodos. */
  livePeriod?: number | null;
  /**
   * Aviso del organizador para los jugadores ("suspendido por lluvia", "se
   * juega en la cancha 2"). Es el POR QUÉ del estado, que hasta ahora no salía
   * de la web del organizador.
   */
  publicNote?: string | null;
  /**
   * Fase del partido. "bye" = el amistoso entre los LIBRES de la fecha: partido
   * real pero fuera de las estadísticas, con su propia regla de puntaje
   * (`byeMode`). Es la marca ESTRUCTURAL — antes el jugador sólo tenía el
   * `publicNote`, que se perdía al reprogramar (ver lib/amistosoLibres.ts).
   */
  stageCode?: string | null;
  /** Regla del amistoso de libres: "double_win" (o null = default) / "real_result". */
  byeMode?: string | null;
  homeTeamId: string;
  awayTeamId: string;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogoUrl?: string | null;
  awayTeamLogoUrl?: string | null;
};

/**
 * Etiqueta del reloj de un partido en vivo: "2T 47'" — la marca del tiempo
 * antecede al minuto (sin acta de períodos queda "47'" pelado). Null si no hay
 * minuto creíble: fuera de 0..200 es un partido de prueba que quedó abierto y
 * el número es ruido (mismo guard que la app Android).
 */
export function liveClockLabel(m: { liveMinute?: number | null; livePeriod?: number | null }): string | null {
  const minute = m.liveMinute;
  if (minute == null || minute < 0 || minute > 200) return null;
  const period = m.livePeriod;
  const tag = period != null && period >= 1 && period <= 4 ? `${period}T ` : "";
  return `${tag}${minute}'`;
}

export type TopScorerRow = {
  /** null = goles arrastrados de un torneo migrado, de alguien sin ficha en la liga (144). */
  playerId: string | null;
  playerName: string;
  photoUrl: string | null;
  /** null = goleador arrastrado del que todavia no se sabe el equipo (157). */
  teamId: string | null;
  teamName: string | null;
  goals: number;
};

export type PlayerCardStats = {
  matchesPlayed: number;
  minutesPlayed: number;
  goals: number;
  assists: number;
  yellowCards: number;
  redCards: number;
  averageRating: number | null;
  seasonScore: number | null;
  isOnFire: boolean;
  goalsAgainst: number | null;
  cleanSheets: number | null;
};

export type PlayerStats = {
  playerId: string;
  tournamentId: string | null;
  /** true = estos ceros NO son "jugué 0 partidos": el usuario no juega en el
   *  contexto activo. La tarjeta no debe generarse con esto. */
  isSpectator?: boolean;
  membership?: "player" | "fan" | "browse_only" | "spectator";
  stats: PlayerCardStats;
  suspension?: { reason: string; remainingMatches: number | null } | null;
};

import type { CardBackState } from "../pages/player/PlayerCardBack";

export type PlayerProfile = {
  id: string;
  displayName: string | null;
  position: string | null;
  jerseyNumber: number | null;
  membership?: "player" | "fan" | "browse_only" | "spectator";
  /** null salvo que JUEGUE en ese equipo (a un hincha no se le atribuye). */
  teamName: string | null;
  leagueName: string | null;
  tournamentName: string | null;
  bio: string | null;
  photoUrl: string | null;
  /** Reverso de Mi Tarjeta. Opcional: backend viejo → frente, como hoy. */
  cardBack?: CardBackState | null;
};

/* Torneo EXPLÍCITO en los GET por-torneo (auditoría del contexto, 13-09-2026):
 * sin él, el server derivaba de la fila global user_active_contexts — que la
 * app o la web pueden pisar entre el pedido y la respuesta — y el caché SWR
 * guardaba la tabla de OTRO torneo bajo la clave de este. El eco de la
 * respuesta se valida: si vino de otro torneo (el pedido ya no existe), se
 * tira error en vez de pintar y cachear datos ajenos. */
function torneoQs(tournamentId?: string | null, sep: "?" | "&" = "?"): string {
  return tournamentId ? `${sep}tournamentId=${encodeURIComponent(tournamentId)}` : "";
}

function verificarEco(
  pedido: string | null | undefined,
  eco: { tournamentId?: string | null } | null | undefined,
): void {
  if (pedido && eco?.tournamentId && eco.tournamentId.toUpperCase() !== pedido.toUpperCase()) {
    throw new Error("Este torneo ya no está disponible. Elegí otro desde Cambiar torneo.");
  }
}

export async function fetchPlayerHome(tournamentId?: string | null): Promise<PlayerHome> {
  const res = await apiFetch<PlayerHome>(`/api/v1/me/home${torneoQs(tournamentId)}`);
  verificarEco(tournamentId, res.context);
  return res;
}

export async function fetchPlayerStandings(tournamentId?: string | null): Promise<PlayerStandings> {
  const res = await apiFetch<PlayerStandings & { context?: { tournamentId?: string | null } }>(
    `/api/v1/me/standings${torneoQs(tournamentId)}`,
  );
  verificarEco(tournamentId, res.context);
  return res;
}

/** Fixture completo del TORNEO (no solo mi equipo), como la pestaña Liga de la app. */
export async function fetchTournamentMatches(
  kind: "upcoming" | "past",
  tournamentId?: string | null,
): Promise<{ matches: PlayerMatchRow[] }> {
  const res = await apiFetch<{ matches: PlayerMatchRow[]; context?: { tournamentId?: string | null } }>(
    `/api/v1/me/tournament/matches/${kind}${torneoQs(tournamentId)}`,
  );
  verificarEco(tournamentId, res.context);
  return res;
}

/** Goleadores de la temporada (misma suma que la tabla anual). null = no hay anual. */
export type PlayerAnnualTopScorers = {
  name: string;
  tournamentName: string;
  tournaments: Array<{ id: string; name: string }>;
  topScorers: TopScorerRow[];
};

export async function fetchTopScorers(
  tournamentId?: string | null,
): Promise<{ topScorers: TopScorerRow[]; annual?: PlayerAnnualTopScorers | null }> {
  const res = await apiFetch<{
    topScorers: TopScorerRow[];
    annual?: PlayerAnnualTopScorers | null;
    context?: { tournamentId?: string | null };
  }>(
    `/api/v1/me/top-scorers${torneoQs(tournamentId)}`,
  );
  verificarEco(tournamentId, res.context);
  return res;
}

export async function fetchPlayerStats(tournamentId?: string | null): Promise<PlayerStats> {
  const res = await apiFetch<PlayerStats & { tournamentId?: string | null }>(
    `/api/v1/me/stats${torneoQs(tournamentId)}`,
  );
  // El eco acá es el tournamentId top-level de la respuesta.
  verificarEco(tournamentId, res.tournamentId ? { tournamentId: res.tournamentId } : null);
  return res;
}

export async function fetchPlayerProfile(): Promise<PlayerProfile> {
  return apiFetch<PlayerProfile>("/api/v1/me/player-profile", { cacheMs: IDENTIDAD_MS });
}

/* ---------- Formato de fechas (hora de pared) ---------- */

export function formatWallClockDate(iso: string | null): string {
  if (!iso) return "Fecha a confirmar";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Fecha a confirmar";
  return d.toLocaleDateString("es-UY", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

export function formatWallClockTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return (
    d.toLocaleTimeString("es-UY", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC",
    }) + " hs"
  );
}

/* ---------- Detalle de partido y equipo (navegación del Home) ---------- */

export type MatchIncident = {
  id: string;
  type: "goal" | "own_goal" | "assist" | "yellow_card" | "red_card" | "suspension" | "substitution" | "observation";
  minuteLabel: string;
  // Para resolver el ESCUDO del evento contra home/away (paridad Android).
  teamId: string | null;
  teamName: string | null;
  playerName: string | null;
  // Cara del protagonista; null → la fila cae al badge del tipo de evento.
  playerPhotoUrl: string | null;
  notes: string | null;
  playerInName: string | null;
  playerOutName: string | null;
  createdAt: string;
};

export type PlayerMatchDetail = PlayerMatchRow & {
  tournamentName?: string | null;
  leagueName?: string | null;
  opponentName?: string | null;
  isHome?: boolean;
  myTeamId: string | null;
  incidents: MatchIncident[];
  homeKitColor?: string | null;
  homeKitColor2?: string | null;
  homeKitDesign?: string | null;
  homeKitAlt?: string | null;
  awayKitColor?: string | null;
  awayKitColor2?: string | null;
  awayKitDesign?: string | null;
  awayKitAlt?: string | null;
  kitAviso?: { sePisan: boolean; cambia: "home" | "away" | null; alternativa: string | null } | null;
  /**
   * Jugadores que NO pueden jugar este partido por sanción, de los dos
   * equipos. El endpoint ya los mandaba —la app los muestra desde siempre—
   * pero la web no declaraba el campo y por lo tanto no los mostraba: un
   * jugador abría el partido y no se enteraba de que le faltaba un compañero.
   */
  unavailablePlayers?: PlayerMatchUnavailable[] | null;
};

/** Una baja por sanción (player_suspensions), tal como la manda el backend. */
export type PlayerMatchUnavailable = {
  id: string;
  playerId: string;
  playerName: string;
  teamId: string | null;
  teamName: string | null;
  photoUrl: string | null;
  reason: string | null;
  /** Cuántas fechas le quedan contando ésta. null = no se puede calcular. */
  remainingMatches: number | null;
};

export async function fetchPlayerMatchDetail(matchId: string): Promise<PlayerMatchDetail> {
  return apiFetch<PlayerMatchDetail>(`/api/v1/me/matches/${matchId}`);
}

export type PlayerTeamSquadRow = {
  playerId: string;
  displayName: string;
  position: string | null;
  roleInTeam: string | null;
  jerseyNumber: number | null;
  photoUrl: string | null;
};

export type PlayerTeam = {
  team: {
    id: string | null; name: string | null; logoUrl: string | null; photoUrl?: string | null;
    /** Camisetas (072/073): color + segundo color + diseño, titular y alternativa. */
    kitColor?: string | null; kitColor2?: string | null; kitDesign?: string | null;
    kitAlt?: string | null; kitAltColor2?: string | null; kitAltDesign?: string | null;
    /** "withdrawn" = el equipo se retiró del torneo. Se avisa en la ficha:
     *  desde la tabla se llega igual, y el plantel y el próximo partido de un
     *  retirado se leen como los de un rival que todavía va a jugar. */
    entryStatus?: string | null;
  };
  competitiveSummary: { position: number | null; points: number; played: number };
  nextMatch: PlayerNextMatch | null;
  squad: PlayerTeamSquadRow[];
  /** Torneo de esta ficha (el cuerpo técnico es por torneo). */
  tournamentId?: string | null;
  /** DT vigente (138). Va aparte del plantel: no es un jugador. */
  /** El 1er DT. Queda aparte de `coaches` por los clientes viejos. */
  coach?: { playerId: string; displayName: string; photoUrl: string | null; alsoPlays: boolean } | null;
  /** Los hasta tres DT vigentes, ya numerados (1er, 2do, 3er). */
  coaches?: Array<{ playerId: string; displayName: string; photoUrl: string | null; alsoPlays: boolean; orden: number }>;
  viewerIsCaptain?: boolean;
  viewerIsDelegate?: boolean;
  /** El que mira es el DT vigente. */
  viewerIsCoach?: boolean;
};

/** Declara las camisetas del equipo (capitán u organizador): color, segundo
 *  color y diseño de la titular y la alternativa. */
export type KitPayload = {
  primary: string | null;
  primaryColor2?: string | null;
  primaryDesign?: string | null;
  alternate: string | null;
  alternateColor2?: string | null;
  alternateDesign?: string | null;
};
export async function updateTeamKitColors(payload: KitPayload): Promise<void> {
  await apiFetch("/api/v1/me/team/colors", { method: "PUT", body: payload });
}

export async function updateMyTeamPhoto(payload: {
  assetId: string | null;
  sourceAssetId?: string | null;
  crop?: { x: number; y: number; w: number; h: number } | null;
}): Promise<void> {
  await apiFetch("/api/v1/me/team/photo", { method: "PUT", body: payload });
}

export async function fetchPlayerTeam(teamId?: string, tournamentId?: string | null): Promise<PlayerTeam> {
  const qs = teamId ? `?teamId=${encodeURIComponent(teamId)}` : "";
  return apiFetch<PlayerTeam>(`/api/v1/me/team${qs}${torneoQs(tournamentId, qs ? "&" : "?")}`);
}

/* ---------- Ficha de un jugador cualquiera (bottom sheet) ---------- */

/**
 * Ficha corta de un jugador del plantel o del ranking — el mismo payload que
 * consume PlayerCardSheet.kt en Android. Las stats son las del TORNEO ACTIVO
 * del que mira (las resuelve el backend con el contexto), no las de toda la
 * carrera del jugador.
 */
export type PlayerCard = {
  playerId: string;
  displayName: string;
  position: string | null;
  jerseyNumber: number | null;
  roleInTeam: string | null;
  teamName: string | null;
  photoUrl: string | null;
  cutoutUrl?: string | null;
  stats: {
    matchesPlayed: number;
    goals: number;
    assists: number;
    yellowCards: number;
    redCards: number;
  };
};

export async function fetchPlayerCard(playerId: string): Promise<PlayerCard> {
  return apiFetch<PlayerCard>(`/api/v1/players/${encodeURIComponent(playerId)}`);
}

/** Perfil de comunidad (lo que el jugador carga de sí mismo). */
export type CommunityProfile = {
  city: string | null;
  zone: string | null;
  preferredPositions: string[];
  dominantFoot: "right" | "left" | "both" | null;
  birthDate: string | null;
  ageCategory: string | null;
  modalities: string[];
  skillLevel: "casual" | "amateur" | "competitive" | null;
  availableDays: string[];
  timeSlots: string[];
  availabilityStatus: "available" | "seeking_team" | "friendlies_only" | "unavailable" | null;
  visibility: "public" | "league_only" | "private" | null;
  contactWhatsapp: string | null;
  contactInstagram: string | null;
  showWhatsapp: boolean;
  showInstagram: boolean;
  updatedAt?: string | null;
};

/**
 * Ficha pública (solo lectura) de un jugador. `community` viene en null si el
 * jugador nunca cargó su perfil o si su visibilidad no alcanza al que mira
 * (la decide el backend, acá no se filtra nada).
 */
export type PublicPlayerProfile = {
  playerId: string;
  displayName: string;
  position: string | null;
  jerseyNumber: number | null;
  teamName: string | null;
  photoUrl: string | null;
  cutoutUrl?: string | null;
  bio: string | null;
  community: CommunityProfile | null;
};

export async function fetchPublicPlayerProfile(playerId: string): Promise<PublicPlayerProfile> {
  return apiFetch<PublicPlayerProfile>(
    `/api/v1/players/${encodeURIComponent(playerId)}/public-profile`,
  );
}

/**
 * El CAPITÁN nombra o quita delegado en su equipo (torneo activo). El delegado
 * puede cargar la planilla igual que él → el equipo no depende de una sola
 * persona. Lo valida el backend: si el que llama no es capitán, 403.
 */
export async function setTeamMemberRole(
  playerId: string,
  role: "delegate" | "player",
): Promise<void> {
  await apiFetch(`/api/v1/me/team/members/${encodeURIComponent(playerId)}/role`, {
    method: "PUT",
    body: { role },
  });
}

/* ---------- Rankings extra de la pestaña Liga ---------- */

export type TopAssistRow = {
  playerId: string;
  playerName: string;
  photoUrl: string | null;
  teamId: string;
  teamName: string;
  assists: number;
};

export async function fetchTopAssists(tournamentId?: string | null): Promise<{ topAssists: TopAssistRow[] }> {
  const res = await apiFetch<{ topAssists: TopAssistRow[]; context?: { tournamentId?: string | null } }>(
    `/api/v1/me/top-assists${torneoQs(tournamentId)}`,
  );
  verificarEco(tournamentId, res.context);
  return res;
}

export type GoalkeeperPlayer = {
  playerId: string;
  playerName: string;
  jerseyNumber: number | null;
  photoUrl: string | null;
};

/**
 * "Arqueros" = VALLA MENOS VENCIDA. Las métricas (promedio de goles en contra,
 * vallas invictas) son del EQUIPO — FORJA no captura quién atajó cada partido —
 * y se atribuyen al/los arquero(s) del plantel: puede haber 0, 1 o varios.
 * Ordena por `concededPerMatch` (GC/PJ); `rank` viene con dense-rank del
 * backend y empata cuando coinciden las tres métricas del orden, así que NO
 * usar el índice del array como puesto.
 */
export type GoalkeeperRow = {
  rank: number;
  teamId: string;
  teamName: string;
  logoUrl: string | null;
  played: number;
  cleanSheets: number;
  goalsAgainst: number;
  /** GC/PJ con dos decimales: la métrica que ordena el ranking. */
  concededPerMatch: number;
  goalkeepers: GoalkeeperPlayer[];
};

export type TopGoalkeepers = {
  goalkeepers: GoalkeeperRow[];
  /** Jugaron menos del mínimo: se muestran aparte, sin puesto. */
  belowMinimum: Array<Omit<GoalkeeperRow, "rank">>;
  minimumPlayed: number;
  maxPlayed: number;
};

export async function fetchGoalkeepers(tournamentId?: string | null): Promise<TopGoalkeepers> {
  const res = await apiFetch<TopGoalkeepers & { context?: { tournamentId?: string | null } }>(
    `/api/v1/me/goalkeepers${torneoQs(tournamentId)}`,
  );
  verificarEco(tournamentId, res.context);
  // Contra un backend viejo falta el promedio: se recalcula, no se rompe.
  return normalizarValla(res);
}

/* ---------- Contextos disponibles (cambiar de liga / torneo) ---------- */

/**
 * Una fila de /me/contexts: liga + torneo + equipo donde el usuario tiene rol.
 * Un mismo torneo puede venir repetido si tiene varios roles; y `browseOnly`
 * marca los torneos de su liga donde NO tiene rol (entra como espectador).
 */
export type AvailableContext = {
  leagueId: string;
  leagueName: string;
  leagueLogoUrl: string | null;
  isPublic: boolean;
  tournamentId: string | null;
  tournamentName: string | null;
  modalityLabel: string | null;
  tournamentYear: number | null;
  tournamentStatus: string | null;
  categoryLabel: string | null;
  /** Competencia (102): el campeonato paralelo de la liga ("Femenino Martes"). */
  competitionName: string | null;
  teamId: string | null;
  teamName: string | null;
  teamLogoUrl: string | null;
  /** DT vigente de ese equipo en ese torneo (138). */
  isCoach?: boolean;
  roles: string[];
  /** Rol DENTRO del equipo en las filas del jugador: 'captain' | 'delegate' | 'player'. */
  roleInTeam?: string | null;
  browseOnly?: boolean;
};

export async function fetchAvailableContexts(): Promise<AvailableContext[]> {
  const res = await apiFetch<{ contexts: AvailableContext[] }>("/api/v1/me/contexts", { cacheMs: IDENTIDAD_MS });
  return res.contexts ?? [];
}

/** Cambia el contexto activo (liga, y opcionalmente torneo y equipo). */
export async function setActiveContext(input: {
  leagueId: string;
  tournamentId?: string | null;
  teamId?: string | null;
}): Promise<void> {
  await apiFetch("/api/v1/me/active-context", {
    method: "PUT",
    body: {
      leagueId: input.leagueId,
      tournamentId: input.tournamentId ?? null,
      teamId: input.teamId ?? null,
    },
  });
}

/* ---------- Mis ligas: catálogo público, seguir y favoritas ---------- */

/** Una liga del catálogo público (`/api/v1/public/leagues`). */
export type PublicLeague = {
  id: string;
  name: string;
  logoUrl: string | null;
  activeTournaments: number;
};

/**
 * Catálogo de ligas PÚBLICAS (is_public = 1). Es el mismo que usa el selector
 * de la app para "Explorar ligas públicas": una liga privada no figura acá,
 * sólo se llega por link directo (ver migración 041).
 *
 * Se pide UNA vez por apertura del selector y se filtra en memoria, como en
 * Android: el catálogo es chico y el server lo tiene con micro-cache.
 */
export async function fetchPublicLeagues(): Promise<PublicLeague[]> {
  const res = await apiFetch<{ leagues: PublicLeague[] }>("/api/v1/public/leagues");
  return res.leagues ?? [];
}

// El buscador de equipos vive en `api/public.ts` junto con el resto de lo que
// se sirve SIN sesión: la misma función la usa la hoja del jugador (acá) y la
// página pública, que corre sin token.
export { searchPublicTeams, type PublicTeam } from "./public";

/**
 * Jugadores cargados en FORJA, para el cartel del login (`/api/v1/public/stats`).
 * Sin sesión a propósito: el login se ve antes de entrar. Devuelve null si no
 * se pudo saber — el cartel se esconde en vez de mostrar un número inventado.
 */
export async function fetchJugadoresCargados(): Promise<number | null> {
  try {
    const res = await apiFetch<{ jugadores?: number }>("/api/v1/public/stats", { withAuth: false });
    return typeof res.jugadores === "number" ? res.jugadores : null;
  } catch {
    return null;
  }
}

/** Ids de las ligas marcadas como favoritas (ordenan el selector). */
export async function fetchLeagueFavorites(): Promise<string[]> {
  const res = await apiFetch<{ leagueIds: string[] }>("/api/v1/me/league-favorites", { cacheMs: PENDIENTES_MS });
  return res.leagueIds ?? [];
}

export async function addLeagueFavorite(leagueId: string): Promise<void> {
  await apiFetch(`/api/v1/me/league-favorites/${leagueId}`, { method: "PUT" });
}

export async function removeLeagueFavorite(leagueId: string): Promise<void> {
  await apiFetch(`/api/v1/me/league-favorites/${leagueId}`, { method: "DELETE" });
}

/**
 * Seguir una liga pública como hincha. El backend además deja el CONTEXTO
 * ACTIVO en esa liga con su torneo vigente, así que después de esto alcanza
 * con recargar: no hay que llamar a setActiveContext.
 */
export async function followLeague(leagueId: string): Promise<void> {
  await apiFetch("/api/v1/me/follow-league", { method: "POST", body: { leagueId } });
}

export async function unfollowLeague(leagueId: string): Promise<void> {
  await apiFetch(`/api/v1/me/follow-league/${leagueId}`, { method: "DELETE" });
}

/* ---------- Fair Play (081) ---------- */

export type PlayerFairPlayRow = {
  position: number;
  teamId: string;
  teamName: string;
  logoUrl: string | null;
  yellow: number;
  red: number;
  walkovers: number;
  /** Puntos de amonestación. MENOS es mejor. */
  points: number;
};

export type PlayerFairPlay = {
  /** false = la liga no calcula Fair Play → la pestaña ni aparece. */
  enabled: boolean;
  tournamentName: string | null;
  config: { yellow: number; red: number; walkover: number; countWalkover: boolean } | null;
  rows: PlayerFairPlayRow[];
};

export async function fetchPlayerFairPlay(tournamentId?: string | null): Promise<PlayerFairPlay> {
  const res = await apiFetch<PlayerFairPlay & { context?: { tournamentId?: string | null } }>(
    `/api/v1/me/fair-play${torneoQs(tournamentId)}`,
  );
  verificarEco(tournamentId, res.context);
  return res;
}

/* ---------- Cuenta del equipo, para el capitán/delegado (079) ---------- */

export type MiCuentaEntry = {
  id: string;
  kind: "charge" | "payment";
  concept: string;
  amount: number;
  description: string | null;
  voided: boolean;
  createdAt: string;
};

export type MiCuenta = {
  currency: string;
  teamId: string;
  teamName: string;
  charged: number;
  /**
   * OJO: `paid` incluye los descuentos (el backend lo arma como
   * collected + discounts). Para decir "pagado" va `collected`, que es la
   * plata que entro de verdad — un descuento no lo pago nadie.
   */
  paid: number;
  collected: number;
  discounts: number;
  balance: number;
  entries: MiCuentaEntry[];
};

/**
 * La cuenta de MI equipo. Va por contexto activo (sin ids en la URL) y tira 403
 * si el usuario no es capitán ni delegado: la regla de permisos vive en el
 * backend, la pantalla sólo decide si dibuja o no.
 */
export async function fetchMiCuenta(): Promise<MiCuenta> {
  return apiFetch<MiCuenta>("/api/v1/me/team/ledger");
}

// ── Link de hinchas del equipo (088) ───────────────────────────────────
/**
 * El link para invitar amigos a seguir al equipo. Cualquiera del plantel lo ve
 * —el reparto real es cada uno a los suyos—; el backend tira 403 al que no
 * juega ahí. `invitation` viene en null cuando el organizador lo apagó
 * (`disabled: true`), que NO es lo mismo que un error.
 */
export type FanInvitation = {
  invitation: {
    id: string;
    token: string;
    teamName: string;
    leagueName: string;
    /** En liga privada este link es la ÚNICA forma de que alguien siga al equipo. */
    leagueIsPublic?: boolean;
  } | null;
  disabled: boolean;
};

export async function fetchFanInvitation(teamId: string): Promise<FanInvitation> {
  return apiFetch<FanInvitation>(`/api/v1/me/teams/${teamId}/fan-invitation`);
}

// ── Lado capitán: link de fichaje + solicitudes de ingreso ─────────────
// Backend: invitations.routes /me/*. Sólo el capitán (403 al resto, que se
// trata en silencio, como el link de hinchas). El link es de sólo lectura:
// configurarlo y regenerarlo sigue siendo del organizador.
export type CaptainInvitation = {
  invitation: {
    token: string;
    isActive: boolean;
    requiresApproval: boolean;
    maxPlayers: number | null;
  } | null;
};

export type CaptainJoinRequest = {
  id: string;
  teamId: string;
  teamName: string;
  applicantName: string;
  applicantEmail: string;
  claimPlayerName: string | null;
  /** true = el documento tipeado ya está en otra ficha de la liga: por eso
   *  pide aprobación aunque el link no la pida. */
  documentConflict?: boolean;
  /** Contra qué ficha choca (el mail de la otra cuenta llega tapado). */
  documentConflictInfo?: import("../lib/choqueDocumento").DocumentConflictInfo | null;
  /** 'coach' = pide ser el DT (139): no entra al plantel. */
  joinAs?: "coach" | null;
  /** POR QUÉ está pendiente (ver `motivoDeAprobacion`). */
  approvalReason?: "link" | "coach_taken" | "document" | null;
  createdAt: string | null;
};

export async function fetchCaptainInvitation(teamId: string): Promise<CaptainInvitation> {
  return apiFetch<CaptainInvitation>(`/api/v1/me/teams/${teamId}/invitation`);
}

export async function fetchCaptainJoinRequests(
  teamId: string,
): Promise<{ requests: CaptainJoinRequest[] }> {
  return apiFetch<{ requests: CaptainJoinRequest[] }>(`/api/v1/me/teams/${teamId}/join-requests`);
}

/** Un ítem por equipo que dirijo y tiene solicitudes esperando. */
export type EquipoConSolicitudes = {
  teamId: string;
  teamName: string | null;
  leagueId: string | null;
  leagueName: string | null;
  pendientes: number;
  masVieja: string | null;
  /** El nombre del que espera hace más tiempo: con una sola, la tarjeta lo dice. */
  primero: string | null;
};

/**
 * Las solicitudes que me esperan A MÍ, en todos mis equipos. Sin teamId: el
 * ámbito sale de los roles y no del contexto activo (ver el servicio).
 */
export async function fetchJoinRequestsWaitingForMe(): Promise<{
  total: number;
  equipos: EquipoConSolicitudes[];
}> {
  return apiFetch<{ total: number; equipos: EquipoConSolicitudes[] }>(
    `/api/v1/me/join-requests/waiting-for-me`,
  );
}

export async function captainResolveJoinRequest(
  requestId: string,
  approve: boolean,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/api/v1/me/join-requests/${requestId}/${approve ? "approve" : "reject"}`,
    { method: "POST" },
  );
}

// ── Calificación del árbitro (capitán/delegado) ────────────────────────
// Backend: modules/referee-ratings. Los mismos endpoints que usa Android: el
// capitán con iPhone califica desde la PWA, que es su única puerta.
export type RefereeRatingTag = {
  code: string;
  label: string;
  group: "criterio" | "control" | "trato";
  positive: boolean;
};

/** Ejes: 1 = mal, 2 = regular, 3 = bien. */
export type MyRefereeRating = {
  punctuality: number;
  respect: number;
  control: number;
  tags: string[];
  createdAt?: string | null;
};

export type RefereeRatingState = {
  /** Único campo que el cliente mira para decidir si muestra la card. Quién
   *  puede calificar, con qué plazo y en qué liga lo resuelve el backend. */
  canRate: boolean;
  reason: string | null;
  signerName: string | null;
  refereeName: string | null;
  teamId: string | null;
  teamName: string | null;
  closesAt: string | null;
  maxTags: number;
  tags: RefereeRatingTag[];
  myRating: MyRefereeRating | null;
};

export async function fetchRefereeRating(matchId: string): Promise<RefereeRatingState> {
  return apiFetch<RefereeRatingState>(`/api/v1/matches/${matchId}/referee-rating`);
}

export async function submitRefereeRating(
  matchId: string,
  body: { punctuality: number; respect: number; control: number; tags: string[] },
): Promise<{ saved: boolean; rating: MyRefereeRating | null }> {
  return apiFetch(`/api/v1/matches/${matchId}/referee-rating`, { method: "POST", body });
}

// ── ¿Quiénes vinieron? (capitán/delegado, migración 129) ───────────────
// Backend: modules/attendance. Después de un partido jugado SIN cambios
// cargados, el capitán marca quiénes vinieron. Es el dato del puntaje de las
// tarjetas. Mismos endpoints que Android.
export type AttendancePlayer = {
  playerId: string;
  displayName: string;
  shirtNumber: number | null;
  photoUrl: string | null;
  /** Viene marcado. */
  came: boolean;
  /** Tiene un evento en el partido: jugó seguro, no se desmarca. */
  locked: boolean;
};

export type AttendanceState = {
  /** Único campo que el cliente mira para mostrar la tarjeta. */
  canAnswer: boolean;
  reason: string | null;
  teamId: string | null;
  teamName: string | null;
  closesAt: string | null;
  answered: { byName: string | null; at: string } | null;
  /** roster = planilla de este partido (se confirma con un toque) · previous = la del partido anterior · events = sólo los con evento. */
  preselectedFrom: "roster" | "previous" | "events" | null;
  players: AttendancePlayer[];
};

export async function fetchAttendance(matchId: string): Promise<AttendanceState> {
  return apiFetch<AttendanceState>(`/api/v1/matches/${matchId}/attendance`);
}

/** Un partido que el capitán/delegado tiene para cerrar (tarjeta del Inicio). */
export type PendingAttendance = {
  matchId: string;
  teamId: string;
  teamName: string;
  rivalName: string;
  homeTeamName: string;
  awayTeamName: string;
  homeScore: number | null;
  awayScore: number | null;
  closesAt: string;
};

export async function fetchPendingAttendance(tournamentId?: string | null): Promise<{ matches: PendingAttendance[] }> {
  const qs = tournamentId ? `?tournamentId=${encodeURIComponent(tournamentId)}` : "";
  return apiFetch<{ matches: PendingAttendance[] }>(`/api/v1/me/attendance/pending${qs}`, {
    cacheMs: PENDIENTES_MS,
  });
}

export async function submitAttendance(matchId: string, playerIds: string[]): Promise<AttendanceState> {
  return apiFetch<AttendanceState>(`/api/v1/matches/${matchId}/attendance`, {
    method: "PUT",
    body: { playerIds },
  });
}

/**
 * QUÉ TE HACE SONAR EL TELÉFONO (124).
 *
 * El interruptor del navegador (o del sistema) es todo-o-nada: el que se harta
 * de una categoría apaga TODO y después no se entera de que su partido se
 * movió. Estos tres switches existen para que esa decisión se pueda tomar por
 * pedazos. Gobiernan la push; la campana sigue registrando todo.
 */
export type PushPrefs = { partidos: boolean; novedades: boolean; comunidad: boolean };
export type CategoriaPush = keyof PushPrefs;

export async function fetchPushPrefs(): Promise<PushPrefs> {
  return apiFetch("/api/v1/me/push-prefs");
}

export async function setPushPref(
  category: CategoriaPush,
  enabled: boolean,
): Promise<{ ok: true }> {
  return apiFetch("/api/v1/me/push-prefs", { method: "PUT", body: { category, enabled } });
}
